import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { auth, db, doc, onSnapshot } from '../firebase';
import type { User as FirebaseUser } from 'firebase/auth';
import type { Agent, CurrentUser } from '../types';

interface AuthContextType {
  currentUser: CurrentUser | null;
  agentData: Agent | null;
  loading: boolean;
  logout: () => Promise<void>;
  globalNotification: { message: string; type: 'success' | 'error' | 'info' } | null;
  setGlobalNotification: (notification: { message: string; type: 'success' | 'error' | 'info' } | null) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<CurrentUser | null>(null);
  const [agentData, setAgentData] = useState<Agent | null>(null);
  const [loading, setLoading] = useState(true);
  const [globalNotification, setGlobalNotification] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);

  useEffect(() => {
    let isMounted = true;
    let unsubscribeSnapshot: (() => void) | null = null;
    const unsubscribeAuth = auth.onAuthStateChanged(async (user: FirebaseUser | null) => {
      if (user && isMounted) {
        setCurrentUser({ uid: user.uid, email: user.email });
        // Fetch agent data
        const agentDocRef = doc(db, 'agents', user.uid);
        unsubscribeSnapshot = onSnapshot(agentDocRef, (docSnap) => {
          if (!isMounted) return;
          if (docSnap.exists()) {
            setAgentData({ uid: user.uid, ...docSnap.data() } as Agent);
          } else {
            setAgentData(null); 
          }
          setLoading(false);
        }, (error) => {
          if (!isMounted) return;
          console.error("Error fetching agent data:", error);
          setAgentData(null);
          setLoading(false);
          setGlobalNotification({ message: 'Fout bij laden gebruikersgegevens.', type: 'error' });
        });
      } else {
        setCurrentUser(null);
        setAgentData(null);
        setLoading(false);
        if (unsubscribeSnapshot) unsubscribeSnapshot();
      }
    });
    return () => {
      isMounted = false;
      if (unsubscribeSnapshot) unsubscribeSnapshot();
      unsubscribeAuth();
    };
  }, []);

  const logout = async () => {
    try {
      await auth.signOut();
      setCurrentUser(null);
      setAgentData(null);
      setGlobalNotification({ message: 'Succesvol uitgelogd.', type: 'success' });
    } catch (error) {
      console.error("Error signing out: ", error);
      setGlobalNotification({ message: 'Fout bij uitloggen.', type: 'error' });
    }
  };
  
  useEffect(() => {
    if (globalNotification) {
      const timer = setTimeout(() => {
        setGlobalNotification(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [globalNotification]);

  return (
    <AuthContext.Provider value={{ currentUser, agentData, loading, logout, globalNotification, setGlobalNotification }}>
      {children}
    </AuthContext.Provider>
  );
};
